/**
 * @file Config generators for all settings pages
 */

import { ButtonProps } from '@mui/material';
import React from 'react';

import { Link } from 'react-router-dom';

import {
  MODAL__GROUP_CREATE,
  MODAL__LABEL_CREATE,
  MODAL__TOPIC_CREATE,
} from '../../../../constants/routeModalParams';
import { getOpenModalUrl } from '../../../../containers/modals/helpers';
import OpenComposeButton from '../../../../containers/openComposeButton';
import translate from '../../../../i18n/translate';

import { getSettingsTemplatesUrl } from '../../../../routes/urls/settings';
import { getHotelIdFromUrl } from '../../../../utils/hotel';
import { getCanCreateGroup } from '../../../../utils/permissions/group';
import { getCanCreateLabel } from '../../../../utils/permissions/label';
import { getIsBasic } from '../../../../utils/permissions/storage';
import { getCanCreateTemplate } from '../../../../utils/permissions/template';
import { getCanCreateTopic } from '../../../../utils/permissions/topic';
import MuiIcon from '../../../MUI/MuiIcon';
import ButtonAdd from '../ButtonAdd';
import { ConfigCallback, LayoutConfig } from '../types';

/**
 * Generate ButtonProps for settings page
 * (only the create button is used at the moment)
 *
 * @param label The translation key for the button text
 * @param url   Button onClick callback if function, otherwise string for URL
 * @returns     The button props and it's content
 */
const getButtonProps = (label: string, url: string): ButtonProps[] => {
  const commonProps: ButtonProps = {
    children: translate(label),
    key: 'create',
    startIcon: <MuiIcon icon="add" />,
  };

  const dynamicProps =
    typeof url === 'string'
      ? { component: Link, to: url }
      : ({ onClick: url, type: 'button' } as const);

  return [
    {
      ...commonProps,
      ...dynamicProps,
    },
  ];
};

/**
 * Get layout config for the Settings Groups page
 *
 * @param location react-router-dom location object
 * @returns        The desired config
 */
export const getConfigGroups: ConfigCallback = location => {
  const canCreate = getCanCreateGroup();
  const url = getOpenModalUrl(location, MODAL__GROUP_CREATE);
  const subtitleKey = canCreate
    ? 'SETTINGS__GROUPS__SUBTITLE'
    : 'SETTINGS__GROUPS__SUBTITLE_LIMITED_PERMISSION';

  return {
    buttons: canCreate
      ? getButtonProps('SETTINGS__GROUPS__CREATE_GROUP__BUTTON', url)
      : [],
    floatingButton: canCreate ? <ButtonAdd url={url} /> : null,
    icon: 'userGroup',
    subtitle: translate(subtitleKey),
    title: translate('SETTINGS__GROUPS'),
  };
};

/**
 * Get layout config for the Settings Labels page
 *
 * @param location react-router-dom location object
 * @returns        The desired config
 */
export const getConfigLabels: ConfigCallback = location => {
  const canCreate = getCanCreateLabel();
  const url = getOpenModalUrl(location, MODAL__LABEL_CREATE);
  const subtitleKey = canCreate
    ? 'SETTINGS__LABELS__SUBTITLE'
    : 'SETTINGS__LABELS__SUBTITLE_LIMITED_PERMISSION';

  return {
    buttons: canCreate
      ? getButtonProps('SETTINGS__LABELS__CREATE_LABEL__BUTTON', url)
      : [],
    floatingButton: canCreate ? <ButtonAdd url={url} /> : null,
    icon: 'label',
    subtitle: translate(subtitleKey),
    title: translate('SETTINGS__LABELS'),
  };
};

/**
 * Get layout config for the Settings Push Notifications page
 *
 * @returns The desired config
 */
export const getConfigNotificationsPush: ConfigCallback = () => ({
  buttons: [],
  floatingButton: null,
  icon: 'notification',
  shouldShowHeader: false,
  subtitle: null,
  title: translate('SETTINGS__NOTIFICATIONS__PUSH'),
});

/**
 * Get layout config for the Settings Email Notifications page
 *
 * @returns The desired config
 */
export const getConfigNotificationsEmail: ConfigCallback = () => ({
  buttons: [],
  floatingButton: null,
  icon: 'notification',
  shouldShowHeader: false,
  subtitle: null,
  title: translate('SETTINGS__NOTIFICATIONS__EMAIL'),
});

/**
 * Get layout config for the Reservations page
 *
 * @param onClick Onclick
 * @returns       The desired config
 */
export const getConfigReservations = (onClick: () => void): LayoutConfig => ({
  buttons: [
    {
      children: translate('GENERAL__REFRESH'),
      key: 'refresh',
      onClick,
      type: 'button',
    },
  ],
  floatingButton: null,
  icon: 'hotel',
  subtitle: null,
  title: translate('GENERAL__RESERVATIONS'),
});

/**
 * Get layout config for the Settings root page
 *
 * @returns The desired config
 */
export const getConfigRoot: ConfigCallback = () => ({
  buttons: [],
  floatingButton: null,
  icon: null,
  subtitle: null,
  title: translate('GENERAL__SETTINGS'),
});

/**
 * Get layout config for the Settings Templates page
 *
 * @returns The desired config
 */
export const getConfigTemplates: ConfigCallback = () => ({
  buttons: [],
  floatingButton: getCanCreateTemplate() ? (
    <OpenComposeButton nodeType="TEMPLATE" />
  ) : null,
  icon: 'template',
  subtitle: translate('SETTINGS__TEMPLATES__SUBTITLE'),
  title: translate('SETTINGS__TEMPLATES'),
});

/**
 * Get layout config for the Settings Templates Recurring page
 *
 * @param _location react-router-dom location object
 * @param _navigate react-router-dom navigate function
 * @param isTablet  Whether the device is a tablet
 * @returns         The desired config
 */
export const getConfigTemplatesRecurring: ConfigCallback = (
  _location,
  _navigate,
  isTablet,
) => ({
  buttons: [
    {
      as: Link,
      children: translate('GENERAL__BACK'),
      key: 'back',
      startIcon: <MuiIcon icon="arrowLeft" />,
      to: getSettingsTemplatesUrl(getHotelIdFromUrl(), isTablet),
    },
  ],
  floatingButton: null,
  icon: 'template',
  settingsBackUrl: '/templates',
  subtitle: translate('SETTINGS__TEMPLATES__RECURRING__SUBTITLE'),
  title: translate('RRULE__ACTION__CREATE'),
});

/**
 * Get layout config for the Settings Topics page
 *
 * @param location react-router-dom location object
 * @returns        The desired config
 */
export const getConfigTopics: ConfigCallback = location => {
  const canCreate = getCanCreateTopic();
  const url = getOpenModalUrl(location, MODAL__TOPIC_CREATE);

  const subtitleKey = getIsBasic()
    ? 'SETTINGS__TOPICS__SUBTITLE_BASIC'
    : 'SETTINGS__TOPICS__SUBTITLE';

  return {
    buttons: canCreate ? getButtonProps('GENERAL__CREATE', url) : [],
    floatingButton: canCreate ? <ButtonAdd url={url} /> : null,
    icon: 'topic',
    subtitle: translate(subtitleKey),
    title: translate('SETTINGS__TOPICS'),
  };
};

/**
 * Get layout config for the Settings Users page
 *
 * @returns The desired config
 */
export const getConfigTranslation: ConfigCallback = () => ({
  buttons: [],
  floatingButton: null,
  icon: 'translate',
  subtitle: translate('SETTINGS__AUTOMATIC_TRANSLATIONS__SUBTITLE'),
  title: translate('SETTINGS__AUTOMATIC_TRANSLATIONS'),
});

/**
 * Get layout config for the Settings Users page
 *
 * @returns The desired config
 */
export const getConfigUsers: ConfigCallback = () => ({
  buttons: [],
  floatingButton: null,
  icon: 'user',
  subtitle: translate('SETTINGS__PEOPLE__SUBTITLE'),
  title: translate('SETTINGS__PEOPLE'),
});
